// src/pages/PoolPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function PoolPage() {
  const { user } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "exspirace", dir: "asc" });
  const [page, setPage] = useState(1);
  const pageSize = 100;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!user) {
          setErr("Uživatel není přihlášen.");
          setLoading(false);
          return;
        }

        const firma = user.firma;
        const code = user.pharmacyCode;

        if (!firma || !code) {
          throw new Error("Chybí firma nebo kód pobočky.");
        }

        const url = `/api/pool/${encodeURIComponent(firma)}/${encodeURIComponent(code)}`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(await res.text());
        }

        const json = await res.json();
        const mapped = (Array.isArray(json) ? json : [])
          .map(mapItem)
          .filter(Boolean);

        if (!cancelled) setRows(mapped);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  function mapItem(item) {
    if (!item) return null;
    const exs = item.Exspirace ?? null;

    return {
      pdk: item.PDK ?? "",
      nazev: item.Název ?? "",
      mnozstvi: Number(item.Množství ?? 0),
      sarze: item.Šarže ?? "",
      exspirace: exs ? new Date(exs) : null,
      ncsd: item.NCSD != null ? Number(item.NCSD).toFixed(2) : null,
      targetPharmacyCode: item.targetPharmacyCode || "",
      stav: item.stav ?? "",
      duvod: item.Důvod ?? item.duvod ?? "",
    };
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        (r.pdk && String(r.pdk).toLowerCase().includes(s)) ||
        (r.nazev && r.nazev.toLowerCase().includes(s))
    );
  }, [rows, q]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sort.key === "exspirace") {
        const av = a.exspirace ? a.exspirace.getTime() : 0;
        const bv = b.exspirace ? b.exspirace.getTime() : 0;
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      }
      if (sort.key === "nazev") {
        const cmp = (a.nazev || "").localeCompare(b.nazev || "", "cs");
        return sort.dir === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const visible = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, pageSafe]);

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  return (
    <div className="p-4 max-w-[1150px]">
      <h1 className="text-2xl font-bold mb-4">POOL přehled</h1>

      <div className="flex items-center gap-3 mb-3">
        <input
          className="w-full max-w-xl border rounded-lg px-3 py-2"
          placeholder="Hledat podle názvu nebo PDK…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <div className="text-sm opacity-70">
          {filtered.length} / {rows.length}
        </div>
      </div>

      {loading && <div>Načítám data…</div>}
      {err && <div className="text-red-600">Chyba: {err}</div>}

      {!loading && !err && (
        <>
          <div className="rounded-lg border">
            <table className="table-fixed w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th
                    onClick={() => toggleSort("nazev")}
                    active={sort.key === "nazev"}
                    dir={sort.dir}
                    className="w-[370px]"
                  >
                    Název
                  </Th>
                  <th className="text-left px-3 py-2 w-[90px]">PDK</th>
                  <th className="text-right px-3 py-2 w-[80px]">Množství</th>
                  <th className="text-right px-3 py-2 w-[110px]">NCSD</th>
                  <th className="text-left px-3 py-2 w-[100px]">Šarže</th>
                  <Th
                    onClick={() => toggleSort("exspirace")}
                    active={sort.key === "exspirace"}
                    dir={sort.dir}
                    className="w-[100px]"
                  >
                    Exspirace
                  </Th>
                  <th className="text-left px-3 py-2 w-[70px]">Cílová pobočka</th>
                  <th className="text-left px-3 py-2 w-[90px]">Stav</th>
                  <th className="text-left px-3 py-2 w-[120px]">Důvod</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={`${r.pdk}-${i}`} className="odd:bg-white even:bg-gray-50">
                    <td className="px-3 py-2 break-words w-[350px]">{r.nazev}</td>
                    <td className="px-3 py-2 w-[90px]">{r.pdk}</td>
                    <td className="px-3 py-2 text-right w-[80px]">{r.mnozstvi}</td>
                    <td className="px-3 py-2 text-right w-[110px]">{fmtMoney(r.ncsd)}</td>
                    <td className="px-3 py-2 w-[100px]">{r.sarze}</td>
                    <td className="px-3 py-2 whitespace-nowrap w-[100px]">
                      {fmtDate(r.exspirace)}
                    </td>
                    <td className="px-3 py-2 w-[70px]">{r.targetPharmacyCode}</td>
                    <td className="px-3 py-2 w-[90px]">{r.stav}</td>
                    <td className="px-3 py-2 w-[100px]">{r.duvod}</td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-500" colSpan={9}>
                      Nic nenalezeno.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded"
                disabled={pageSafe <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Předchozí
              </button>
              <div className="text-sm">
                Strana {pageSafe}/{totalPages}
              </div>
              <button
                className="px-3 py-1 border rounded"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Další
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Th({ children, onClick, active, dir, className = "" }) {
  return (
    <th
      className={`text-left px-3 py-2 select-none cursor-pointer ${className}`}
      onClick={onClick}
    >
      <span className="inline-flex items-center gap-1">
        {children} {active ? (dir === "asc" ? "▲" : "▼") : ""}
      </span>
    </th>
  );
}

// --- utils ---
function fmtDate(d) {
  if (!d) return "—";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function fmtMoney(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")} Kč`;
}
